/**
 * Accepting a staff invitation: the screen the emailed link lands on.
 *
 * Every demo email links here too, including a repeat request from somebody whose login
 * is already active (owner, 24 October 2026). So the screen is the same whoever arrives:
 * their email address already filled in, one field to choose a password, and Sign in.
 * The address comes from the link itself (`invitationDetails`), never from the URL, and
 * looking it up does not use the link.
 *
 * Redeeming activates the user and their membership **together**, in one transaction.
 * A user activated without their membership is a login that can reach nothing; a
 * membership activated without the user is a permission grant to an account nobody can
 * sign into. The server does that; this screen collects a password and gets out of the
 * way.
 *
 * A session comes back on success, so the new member lands inside the application
 * already signed in. Making somebody set a password and then immediately type it again
 * is a pointless step at the exact moment they are forming an impression of the product.
 *
 * The failure wording is deliberately one message for every cause. A token that never
 * existed, one already used, and one that expired all return the same refusal, because
 * distinguishing them tells whoever is holding a link which kind of link they have.
 */

import {
  acceptInvitationAndStore,
  invitationDetails,
  useApi,
  userMessage,
} from '@legalcrm/api';
import {
  Card,
  CardBody,
  Form,
  FormActions,
  FormInput,
  Input,
  Spinner,
  useZodForm,
} from '@legalcrm/ui';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { z } from 'zod';


const schema = z.object({
  full_name: z.string(),
  password: z.string().min(12, 'Use at least 12 characters'),
});

type Values = z.infer<typeof schema>;

type Details = { email: string; full_name: string };

export function AcceptInvitationPage() {
  const [params] = useSearchParams();
  const api = useApi();
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [details, setDetails] = useState<Details | null>(null);
  const [refused, setRefused] = useState(false);

  const token = params.get('token') ?? '';

  const form = useZodForm(schema, {
    defaultValues: { full_name: '', password: '' },
  });

  useEffect(() => {
    if (!token) return;
    let current = true;
    invitationDetails(api, token).then(
      (found) => current && setDetails(found),
      () => current && setRefused(true),
    );
    return () => {
      current = false;
    };
  }, [api, token]);

  // A link with no token is a link somebody has mangled in a mail client. Say so
  // plainly rather than showing a form that cannot possibly succeed.
  if (!token) {
    return (
      <Frame title="This link is incomplete">
        <p className="text-sm text-ink-muted">
          The address is missing the part that identifies your invitation. Copying the
          link out of the email again usually fixes it &mdash; some mail apps cut long
          links in half.
        </p>
      </Frame>
    );
  }

  if (refused) {
    return (
      <Frame title="This link is no longer valid">
        <p className="text-sm text-ink-muted">
          It has expired or has already been used.{' '}
          <Link to="/login" className="text-primary hover:underline">
            Go to sign in
          </Link>
          , or ask for a new link.
        </p>
      </Frame>
    );
  }

  if (!details) {
    return (
      <Frame title="Choose your password">
        <div className="flex justify-center py-6">
          <Spinner />
        </div>
      </Frame>
    );
  }

  return (
    <Frame title="Choose your password" description="Then you are signed straight in.">
      <Form
        form={form}
        ariaLabel="Choose your password and sign in"
        error={error}
        onSubmit={async (values: Values) => {
          setBusy(true);
          setError(null);
          try {
            await acceptInvitationAndStore(api, {
              token,
              password: values.password,
              full_name: values.full_name || undefined,
            });
            // A full navigation rather than `navigate`: AuthProvider reads the session
            // store once, on mount, so a client-side route change would leave it still
            // believing there is no session.
            window.location.assign('/');
          } catch (caught) {
            setError(caught);
          } finally {
            setBusy(false);
          }
        }}
      >
        {/* Shown, not editable: the password is set for the login the link was sent to.
            `username` lets a password manager save the pair. */}
        <Input
          label="Email"
          type="email"
          value={details.email}
          readOnly
          autoComplete="username"
        />
        {!details.full_name && (
          <FormInput
            form={form}
            name="full_name"
            label="Your name"
            hint="How colleagues will see you. You can change it later."
            autoComplete="name"
          />
        )}
        <FormInput
          form={form}
          name="password"
          label="Choose your password"
          hint="At least 12 characters."
          type="password"
          autoComplete="new-password"
          autoFocus
        />

        <FormActions submitLabel="Sign in" busy={busy} />
      </Form>

      {Boolean(error) && (
        <p className="mt-4 text-sm text-ink-muted">
          {userMessage(error)}{' '}
          <Link to="/login" className="text-primary hover:underline">
            Go to sign in
          </Link>
          , or ask whoever invited you to send a new link.
        </p>
      )}
    </Frame>
  );
}

function Frame({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mx-auto flex min-h-screen max-w-md items-center px-4 py-10">
      <Card className="w-full">
        <CardBody>
          <h1 className="text-xl font-semibold text-ink">{title}</h1>
          {description && <p className="mb-4 mt-1 text-sm text-ink-muted">{description}</p>}
          {children}
        </CardBody>
      </Card>
    </div>
  );
}
